import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/Toast';
import { simpleAuthService } from '../services/simpleAuthService';
import { parseError } from '../utils/errorHandler';

interface LoginForm {
    userId: string;
    password: string;
}

interface RegisterForm {
    name: string;
    userId: string;
    password: string;
    confirmPassword: string;
}

interface FormErrors {
    name?: string;
    userId?: string;
    password?: string;
    confirmPassword?: string;
}

export const SimpleLoginPage: React.FC = () => {
    const { login } = useAuth();
    const { showError, showSuccess } = useToast();
    const [isRegisterMode, setIsRegisterMode] = useState(false);
    const [loading, setLoading] = useState(false);

    const [loginForm, setLoginForm] = useState<LoginForm>({
        userId: '',
        password: ''
    });

    const [registerForm, setRegisterForm] = useState<RegisterForm>({
        name: '',
        userId: '',
        password: '',
        confirmPassword: ''
    });

    const [errors, setErrors] = useState<FormErrors>({});

    const validateLoginForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!loginForm.userId) {
            newErrors.userId = 'ユーザーIDを入力してください';
        } else if (loginForm.userId.length < 3) {
            newErrors.userId = 'ユーザーIDは3文字以上で入力してください';
        }

        if (!loginForm.password) {
            newErrors.password = 'パスワードを入力してください';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateRegisterForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!registerForm.name) {
            newErrors.name = '名前を入力してください';
        }

        if (!registerForm.userId) {
            newErrors.userId = 'ユーザーIDを入力してください';
        } else if (registerForm.userId.length < 3) {
            newErrors.userId = 'ユーザーIDは3文字以上で入力してください';
        } else if (!/^[a-zA-Z0-9_-]+$/.test(registerForm.userId)) {
            newErrors.userId = 'ユーザーIDは英数字、アンダースコア、ハイフンのみ使用可能です';
        }

        if (!registerForm.password) {
            newErrors.password = 'パスワードを入力してください';
        } else if (registerForm.password.length < 8) {
            newErrors.password = 'パスワードは8文字以上で入力してください';
        } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(registerForm.password)) {
            newErrors.password = 'パスワードは大文字・小文字・数字を含む必要があります';
        }

        if (!registerForm.confirmPassword) {
            newErrors.confirmPassword = 'パスワード確認を入力してください';
        } else if (registerForm.password !== registerForm.confirmPassword) {
            newErrors.confirmPassword = 'パスワードが一致しません';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        console.log('🚀 Login button clicked');

        if (!validateLoginForm()) {
            console.log('❌ Form validation failed');
            return;
        }

        try {
            setLoading(true);
            console.log('🔄 Starting login process...');
            console.log('Environment check:', {
                isDev: import.meta.env.DEV,
                bypassAuth: import.meta.env.VITE_BYPASS_AUTH,
                mode: import.meta.env.MODE
            });

            // 開発モード用の簡易ログイン
            if (import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === 'true') {
                console.log('🔧 Using bypass authentication');
                // モックユーザーでログイン
                const mockUser = { id: loginForm.userId, name: loginForm.userId, role: 'admin' as const };
                const mockTeam = { id: 'default-team', name: 'サンプルチーム', role: 'admin' as const };
                login('mock-token', 'mock-refresh-token', mockUser, mockTeam);
                console.log('✅ Mock login successful');
            } else {
                console.log('🌐 Using real API authentication');
                // 実際のAPI呼び出し
                const authResponse = await simpleAuthService.login({
                    userId: loginForm.userId,
                    password: loginForm.password
                });
                
                console.log('✅ API login successful:', authResponse);
                login(authResponse.token, authResponse.refreshToken, authResponse.user, authResponse.team);
            }
            showSuccess('ログインしました');
        } catch (error) {
            console.error('❌ Login error:', error);
            const appError = parseError(error);
            showError(appError, 'ログインに失敗しました');
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateRegisterForm()) return;

        try {
            setLoading(true);
            // 開発モード用の簡易登録
            if (import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === 'true') {
                showSuccess('アカウントを作成しました（開発モード）');
                setIsRegisterMode(false);
            } else {
                // 実際のAPI呼び出し
                // await authService.register(registerForm.name, registerForm.email, registerForm.password);
                alert('実際の登録機能は今後実装予定です');
            }
        } catch (error) {
            const appError = parseError(error);
            showError(appError, 'アカウント作成に失敗しました');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div>
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                        {isRegisterMode ? 'アカウント作成' : 'ログイン'}
                    </h2>
                    <p className="mt-2 text-center text-sm text-gray-600">
                        草野球チーム管理システム
                    </p>
                </div>

                <div className="mt-8 space-y-6">
                    {/* モード切り替えタブ */}
                    <div className="flex rounded-lg bg-gray-100 p-1">
                        <button
                            type="button"
                            onClick={() => setIsRegisterMode(false)}
                            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${!isRegisterMode
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            ログイン
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsRegisterMode(true)}
                            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${isRegisterMode
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            新規登録
                        </button>
                    </div>

                    {/* ログインフォーム */}
                    {!isRegisterMode && (
                        <form className="space-y-6" onSubmit={handleLogin}>
                            <div>
                                <label htmlFor="login-userId" className="block text-sm font-medium text-gray-700">
                                    ユーザーID
                                </label>
                                <input
                                    id="login-userId"
                                    name="userId"
                                    type="text"
                                    autoComplete="username"
                                    required
                                    value={loginForm.userId}
                                    onChange={(e) => setLoginForm({ ...loginForm, userId: e.target.value })}
                                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                    placeholder="ユーザーID（例: testuser）"
                                />
                                {errors.userId && (
                                    <p className="mt-1 text-sm text-red-600">{errors.userId}</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="login-password" className="block text-sm font-medium text-gray-700">
                                    パスワード
                                </label>
                                <input
                                    id="login-password"
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    required
                                    value={loginForm.password}
                                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                    placeholder="パスワード"
                                />
                                {errors.password && (
                                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                                )}
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? 'ログイン中...' : 'ログイン'}
                                </button>
                            </div>
                        </form>
                    )}

                    {/* 新規登録フォーム */}
                    {isRegisterMode && (
                        <form className="space-y-6" onSubmit={handleRegister}>
                            <div>
                                <label htmlFor="register-name" className="block text-sm font-medium text-gray-700">
                                    名前
                                </label>
                                <input
                                    id="register-name"
                                    name="name"
                                    type="text"
                                    autoComplete="name"
                                    required
                                    value={registerForm.name}
                                    onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                    placeholder="名前"
                                />
                                {errors.name && (
                                    <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="register-userId" className="block text-sm font-medium text-gray-700">
                                    ユーザーID
                                </label>
                                <input
                                    id="register-userId"
                                    name="userId"
                                    type="text"
                                    autoComplete="username"
                                    required
                                    value={registerForm.userId}
                                    onChange={(e) => setRegisterForm({ ...registerForm, userId: e.target.value })}
                                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                    placeholder="ユーザーID（英数字、3文字以上）"
                                />
                                {errors.userId && (
                                    <p className="mt-1 text-sm text-red-600">{errors.userId}</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="register-password" className="block text-sm font-medium text-gray-700">
                                    パスワード
                                </label>
                                <input
                                    id="register-password"
                                    name="password"
                                    type="password"
                                    autoComplete="new-password"
                                    required
                                    value={registerForm.password}
                                    onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                    placeholder="パスワード（8文字以上、大文字・小文字・数字を含む）"
                                />
                                {errors.password && (
                                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                                )}
                            </div>

                            <div>
                                <label htmlFor="register-confirm-password" className="block text-sm font-medium text-gray-700">
                                    パスワード確認
                                </label>
                                <input
                                    id="register-confirm-password"
                                    name="confirmPassword"
                                    type="password"
                                    autoComplete="new-password"
                                    required
                                    value={registerForm.confirmPassword}
                                    onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                                    className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                                    placeholder="パスワード確認"
                                />
                                {errors.confirmPassword && (
                                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                                )}
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? 'アカウント作成中...' : 'アカウント作成'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SimpleLoginPage;